import type { FacetTotals } from "@/types/user";

/** Resolve the facet-type definition for a given facet code via the CMS lookups. */
export const getFacetType = (
  facetCode: string,
  cmsFacets: { facetCode?: string; facet?: string }[],
  facetTypes: { facet?: string }[],
) => {
  const facetType = cmsFacets.find((f) => f.facetCode === facetCode)?.facet;
  return facetTypes.find((f) => f.facet === facetType);
};

/**
 * Minimal, permissive structural shapes so this utility accepts any record that
 * carries the per-ladder `facets` and `ladderProgress` arrays — `FullUserRecord`,
 * `ExtendedUser`, `ClimberWithUserPath`, and codegen `Climber` (which wraps
 * fields in `Maybe<>`) all satisfy it.
 */
interface FacetEntry {
  facet?: string | null;
  score?: (number | null)[] | null;
}

interface FacetLadder {
  ladderId?: string | number | null;
  facets?: (FacetEntry | null)[] | null;
}

interface ProgressEntry {
  ladderId?: string | number | null;
  completeDate?: string | null;
}

export interface FacetSource {
  facets?: (FacetLadder | null)[] | null;
  ladderProgress?: (ProgressEntry | null)[] | null;
}

export interface ResolvedFacet {
  facet: string;
  score: [number, number];
}

/**
 * Resolve a single score per facet type using only the most-recently-completed
 * ladder that contains that facet.
 *
 * Facet scores are stored per-ladder, so the same facet type (e.g. "RIASEC:R")
 * can appear in multiple ladders. Rather than summing across ladders (which
 * double-counts a re-take), we keep only the value from the ladder with the
 * latest `ladderProgress.completeDate`. Facet types unique to a single ladder
 * are passed through unchanged.
 *
 * Recency rules:
 * - Missing/unparseable `completeDate`, or a ladder absent from
 *   `ladderProgress`, is treated as oldest (`-Infinity`).
 * - A later `completeDate` strictly wins; on a tie (including all-undated) the
 *   first ladder encountered wins. We never sum, so there is no double-counting.
 */
export const resolveLatestFacets = (source: FacetSource): ResolvedFacet[] => {
  const facetLadders = source?.facets;
  if (!Array.isArray(facetLadders)) return [];

  // Recency lookup keyed by stringified ladderId (facets.ladderId is a string,
  // ladderProgress.ladderId is a number — coerce both, mirroring getLadderProgress).
  const completionByLadder = (source?.ladderProgress || []).reduce(
    (acc: Record<string, number>, progress) => {
      if (progress?.ladderId === undefined || progress?.ladderId === null) {
        return acc;
      }
      const parsed = progress.completeDate
        ? Date.parse(progress.completeDate)
        : NaN;
      acc[String(progress.ladderId)] = Number.isNaN(parsed)
        ? -Infinity
        : parsed;
      return acc;
    },
    {},
  );

  const best = new Map<string, { ts: number; score: [number, number] }>();

  facetLadders.forEach((ladder) => {
    const ts =
      ladder?.ladderId !== undefined && ladder?.ladderId !== null
        ? (completionByLadder[String(ladder.ladderId)] ?? -Infinity)
        : -Infinity;

    ladder?.facets?.forEach((entry) => {
      const facet = entry?.facet;
      const score = entry?.score;
      if (!facet || !Array.isArray(score) || score.length < 2) return;
      const existing = best.get(facet);
      // Strictly-greater so the first ladder encountered wins on a tie.
      if (!existing || ts > existing.ts) {
        best.set(facet, { ts, score: [score[0] ?? 0, score[1] ?? 0] });
      }
    });
  });

  return Array.from(best.entries()).map(([facet, { score }]) => ({
    facet,
    score,
  }));
};

/**
 * Per-facet totals shaped as `Record<facet, { total, possible }>`, sourced from
 * the latest-completed ladder per facet. Replaces the former summing behavior
 * while preserving the return shape consumers expect.
 */
export const getResolvedFacetTotals = (source: FacetSource): FacetTotals => {
  return resolveLatestFacets(source).reduce(
    (totals: FacetTotals, { facet, score }) => {
      totals[facet] = { total: score[0], possible: score[1] };
      return totals;
    },
    {},
  );
};

/**
 * Latest-completed facet scores sorted by ratio (earned/possible) descending.
 * Shared replacement for the previously-duplicated inline `summarizeFacets`.
 */
export const summarizeFacets = (source: FacetSource): ResolvedFacet[] => {
  return resolveLatestFacets(source).sort((a, b) => {
    const ratioA = a.score[1] === 0 ? 0 : a.score[0] / a.score[1];
    const ratioB = b.score[1] === 0 ? 0 : b.score[0] / b.score[1];
    return ratioB - ratioA;
  });
};
